<template>
  <div class="col-sm-12">

    <!-- ── Búsqueda por RFC ─────────────────────────────── -->
    <div class="card mb-3">
      <div class="card-header">
        <h4 class="mb-0">Pagos Individuales</h4>
      </div>
      <div class="card-body">
        <div class="row g-2 align-items-end">
          <div class="col-md-3">
            <label class="form-label mb-1">RFC del contratante</label>
            <input
              v-model="rfc"
              class="form-control"
              type="text"
              placeholder="Ej. GARC800101XXX"
              maxlength="13"
              :disabled="buscando"
              @keyup.enter="buscarContratante"
            />
          </div>
          <div class="col-auto">
            <button class="btn btn-primary" :disabled="!rfc.trim() || buscando" @click="buscarContratante">
              <vue-feather type="search" size="14" class="me-1" />
              {{ buscando ? 'Buscando...' : 'Buscar' }}
            </button>
          </div>
          <div class="col-auto" v-if="contratante">
            <button class="btn btn-outline-secondary" @click="limpiar">
              <vue-feather type="x" size="14" class="me-1" /> Limpiar
            </button>
          </div>
        </div>
        <div v-if="errorBusqueda" class="alert alert-danger mt-3 mb-0 py-2">{{ errorBusqueda }}</div>
      </div>
    </div>

    <!-- ── Contenido ────────────────────────────────────── -->
    <template v-if="contratante">

      <!-- Datos del contratante -->
      <div class="card mb-3">
        <div class="card-body py-3">
          <div class="row align-items-center">
            <div class="col-md-5">
              <small class="text-muted d-block">Contratante</small>
              <span class="fw-bold fs-6">{{ contratante.nombreCompleto }} </span>
            </div>
            <div class="col-md-2">
              <small class="text-muted d-block">RFC</small>
              <span class="fw-bold">{{ contratante.rfc || rfc }}</span>
            </div>

            <!-- Indicadores -->
            <div class="col-md-5 d-flex gap-3 justify-content-md-end mt-2 mt-md-0">
              <div class="indicador indicador-rojo">
                <div class="indicador-label">Saldo</div>
                <div class="indicador-valor">{{ formatCurrency(saldo) }}</div>
              </div>
              <div class="indicador indicador-verde">
                <div class="indicador-label">Primas en depósito</div>
                <div class="indicador-valor">{{ formatCurrency(primasDeposito) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Registrar pago ─────────────────────────────── -->
      <div class="card mb-3">
        <div class="card-header">
          <h6 class="mb-0 fw-bold">Registrar pago</h6>
        </div>
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-3">
              <label class="form-label mb-1">* Método de pago</label>
              <select class="form-select" v-model="pago.metodoPagoId">
                <option value="" disabled>Seleccione una opción</option>
                <option v-for="mp in metodosPago" :key="mp.id" :value="mp.id">{{ mp.nombre }}</option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label mb-1">* Monto</label>
              <div class="input-group">
                <span class="input-group-text">$</span>
                <input
                  v-model="pago.monto"
                  class="form-control"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                />
              </div>
            </div>
            <div class="col-md-2">
              <label class="form-label mb-1">* Fecha de aplicación</label>
              <input v-model="pago.fechaAplicacion" class="form-control" type="date" />
            </div>
            <div class="col-md-3">
              <label class="form-label mb-1">Concepto</label>
              <input v-model="pago.concepto" class="form-control" type="text" placeholder="Opcional" />
            </div>
            <div class="col-auto">
              <button
                class="btn btn-success"
                :disabled="!pagoValido || registrando"
                @click="registrarPago"
              >
                <vue-feather type="check-circle" size="14" class="me-1" />
                {{ registrando ? 'Registrando...' : 'Registrar' }}
              </button>
            </div>
            <div class="col-md-6">
              <label class="form-label mb-1">Observaciones</label>
              <input v-model="pago.observaciones" class="form-control" type="text" placeholder="Opcional" />
            </div>
          </div>
          <div v-if="mensajePago" class="alert mt-3 mb-0 py-2"
            :class="mensajePago.tipo === 'ok' ? 'alert-success' : 'alert-danger'">
            {{ mensajePago.texto }}
          </div>
        </div>
      </div>

      <!-- ── Historial de pagos ──────────────────────────── -->
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0 fw-bold">Pagos registrados</h6>
          <small class="text-muted">{{ pagosContratante.length }} pago(s)</small>
        </div>
        <div class="card-body p-0">
          <div v-if="cargandoPagos" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
            <span class="ms-2 text-muted small">Cargando pagos...</span>
          </div>
          <div v-else-if="!pagosContratante.length" class="text-center py-4 text-muted small">
            Este contratante no tiene pagos registrados.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-hover table-sm mb-0">
              <thead class="table-secondary">
                <tr>
                  <th>Concepto</th>
                  <th>Fecha de aplicación</th>
                  <th class="text-end">Monto total</th>
                  <th class="text-end">Monto disponible</th>
                  <th>Método de pago</th>
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in pagosContratante" :key="p.id ?? p.pagoId">
                  <td>{{ p.concepto || '—' }}</td>
                  <td>{{ formatDate(p.fechaAplicacion) }}</td>
                  <td class="text-end">{{ formatCurrency(p.montoTotal) }}</td>
                  <td class="text-end">{{ formatCurrency(p.montoDisponible) }}</td>
                  <td>{{ metodoPagoLabel(p.metodoPagoId) }}</td>
                  <td>{{ p.observaciones || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ── Pólizas vigentes ───────────────────────────── -->
      <div class="card">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h6 class="mb-0 fw-bold">Pólizas vigentes</h6>
          <small class="text-muted">{{ polizasActivas.length }} póliza(s)</small>
        </div>
        <div class="card-body p-0">
          <div v-if="cargandoPolizas" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
            <span class="ms-2 text-muted small">Cargando pólizas...</span>
          </div>
          <div v-else-if="!polizasActivas.length" class="text-center py-4 text-muted small">
            No se encontraron pólizas activas para este contratante.
          </div>
          <div v-else class="table-responsive">
            <table class="table table-hover table-sm mb-0">
              <thead class="table-secondary">
                <tr>
                  <th style="width: 90px;">
                    Seleccionar
                    <input type="checkbox" :checked="todasSeleccionadas" @change="toggleTodas" />
                  </th>
                  <th>Núm. póliza</th>
                  <th>Producto</th>
                  <th>Aseguradora</th>
                  <th>Ramo</th>
                  <th>Inicio vigencia</th>
                  <th>Fin vigencia</th>
                  <th class="text-end">Prima periódica</th>
                  <th class="text-end">Prima total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pv in polizasActivas" :key="pv.id">
                  <td>
                    <input type="checkbox" v-model="pv._seleccionada" />
                  </td>
                  <td class="fw-bold">{{ pv.numeroPoliza || '—' }}</td>
                  <td>{{ pv.producto?.name || '—' }}</td>
                  <td>{{ pv.producto?.aseguradora?.nombre || '—' }}</td>
                  <td>{{ pv.producto?.ramo?.nombre || '—' }}</td>
                  <td>{{ formatDate(pv.versionActiva?.condiciones?.inicioVigencia) }}</td>
                  <td>{{ formatDate(pv.versionActiva?.condiciones?.finVigencia) }}</td>
                  <td class="text-end">{{ formatCurrency(pv.versionActiva?.condiciones?.primaPeriodica) }}</td>
                  <td class="text-end">{{ formatCurrency(pv.versionActiva?.condiciones?.primaTotal) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="card-footer d-flex justify-content-end gap-2" v-if="polizasActivas.length">
          <button
            class="btn btn-sm btn-primary"
            :disabled="!haySeleccionadas"
            @click="aplicarSeleccionadas"
          >
            <vue-feather type="check-square" size="14" class="me-1" />
            Aplicar a seleccionadas
          </button>
        </div>
      </div>

    </template>

  </div>
</template>

<script>
import api from '@/axios';
import { fetchAllItems } from '@/utils/listResponse';
import { getCached, setCached } from '@/utils/catalogCache';

function getField(item, key) {
  const found = Object.keys(item).find(k => k.toLowerCase() === key.toLowerCase());
  return found ? item[found] : undefined;
}

export default {
  name: 'PagosIndividuales',

  data() {
    return {
      rfc: '',
      buscando: false,
      errorBusqueda: null,

      contratante: null,
      polizasActivas: [],
      cargandoPolizas: false,

      pagosContratante: [],
      cargandoPagos: false,

      pago: {
        metodoPagoId: '',
        monto: '',
        fechaAplicacion: '',
        concepto: '',
        observaciones: '',
      },
      registrando: false,
      mensajePago: null,

      metodosPago: [],
    };
  },

  computed: {
    nombreContratante() {
      const c = this.contratante;
      if (!c) return '';
      return [c.nombre, c.paterno, c.materno].filter(Boolean).join(' ') || c.rfc || '';
    },

    saldo() {
      return this.polizasActivas.reduce((sum, pv) => sum + (pv.saldo ?? 0), 0);
    },

    primasDeposito() {
      return this.pagosContratante.reduce((sum, p) => sum + (Number(getField(p, 'montoDisponible')) || 0), 0);
    },

    pagoValido() {
      return (
        this.pago.metodoPagoId !== '' &&
        this.pago.monto > 0 &&
        this.pago.fechaAplicacion
      );
    },

    todasSeleccionadas() {
      return this.polizasActivas.length > 0 && this.polizasActivas.every(pv => pv._seleccionada);
    },

    haySeleccionadas() {
      return this.polizasActivas.some(pv => pv._seleccionada);
    },
  },

  created() {
    this.cargarMetodosPago();
  },

  methods: {
    async cargarMetodosPago() {
      const cached = getCached('cat_v2_metodospago');
      if (cached) {
        this.metodosPago = cached;
        return;
      }
      try {
        const { items } = await fetchAllItems('instrumentos');
        this.metodosPago = items
          .filter(i => getField(i, 'isActive') !== false)
          .map(i => ({ id: getField(i, 'id'), nombre: getField(i, 'nombre') }));
        setCached('cat_v2_metodospago', this.metodosPago);
      } catch (err) {
        console.error('Error al cargar los métodos de pago:', err);
      }
    },

    async buscarContratante() {
      const rfcTrimmed = this.rfc.trim().toUpperCase();
      if (!rfcTrimmed) return;
      try {
        this.buscando = true;
        this.errorBusqueda = null;
        this.contratante = null;
        this.pagosContratante = [];
        this.polizasActivas = [];

        const res = await api.get(`contratantes/${rfcTrimmed}`);
        this.contratante = res.data;
        const contratanteId = res.data.id ?? res.data.contratanteId;
        await Promise.all([
          this.cargarPagos(contratanteId),
          this.cargarPolizas(rfcTrimmed),
        ]);
      } catch {
        this.errorBusqueda = 'No se encontró un contratante con ese RFC.';
      } finally {
        this.buscando = false;
      }
    },

    async cargarPagos(contratanteId) {
      if (!contratanteId) return;
      try {
        this.cargandoPagos = true;
        this.pagosContratante = (await fetchAllItems(`pagos/contratante/${contratanteId}`)).items;
      } catch {
        this.pagosContratante = [];
      } finally {
        this.cargandoPagos = false;
      }
    },

    async cargarPolizas(rfc) {
      if (!rfc) return;
      try {
        this.cargandoPolizas = true;
        const { items: data } = await fetchAllItems(`contratantes/${rfc}/polizas`);
        this.polizasActivas = data.map(pv => ({ ...pv, _seleccionada: false }));
      } catch {
        this.polizasActivas = [];
      } finally {
        this.cargandoPolizas = false;
      }
    },

    async registrarPago() {
      if (!this.pagoValido) return;
      const contratanteId = this.contratante.id ?? this.contratante.contratanteId;
      try {
        this.registrando = true;
        this.mensajePago = null;
        const monto = Number(this.pago.monto);
        const hoy = new Date().toISOString().slice(0, 10);
        await api.post(`pagos`, {
          contratanteId,
          concepto: this.pago.concepto || '',
          fechaAplicacion: this.pago.fechaAplicacion,
          montoTotal: monto,
          montoDisponible: monto,
          metodoPagoId: Number(this.pago.metodoPagoId),
          estado: 0,
          observaciones: this.pago.observaciones || '',
          isActive: true,
          created: hoy,
          updated: hoy,
        });
        this.mensajePago = { tipo: 'ok', texto: 'Pago registrado correctamente.' };
        this.pago = { metodoPagoId: '', monto: '', fechaAplicacion: '', concepto: '', observaciones: '' };
        await this.cargarPagos(contratanteId);
      } catch {
        this.mensajePago = { tipo: 'error', texto: 'No se pudo registrar el pago. Verifique los datos e intente de nuevo.' };
      } finally {
        this.registrando = false;
      }
    },

    aplicarSeleccionadas() {
      const seleccionadas = this.polizasActivas.filter(pv => pv._seleccionada);
      if (!seleccionadas.length) return;
      const polizas = seleccionadas.map(pv => ({
        id: pv.versionActiva?.id ?? pv.id,
        numeroPoliza: pv.numeroPoliza,
      }));
      this.$router.push({
        name: 'aplicacionPagosIndividuales',
        query: {
          polizas: JSON.stringify(polizas),
          rfc: this.contratante.rfc || this.rfc,
          primasDeposito: this.primasDeposito,
        },
      });
    },

    toggleTodas(e) {
      const val = e.target.checked;
      this.polizasActivas.forEach(pv => (pv._seleccionada = val));
    },

    limpiar() {
      this.rfc = '';
      this.errorBusqueda = null;
      this.mensajePago = null;
      this.pago = { metodoPagoId: '', monto: '', fechaAplicacion: '', concepto: '', observaciones: '' };
      this.pagosContratante = [];
      this.contratante = null;
      this.polizasActivas = [];
    },

    formatDate(val) {
      if (!val) return '—';
      const d = new Date(val);
      if (isNaN(d)) return val;
      const pad = n => String(n).padStart(2, '0');
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
    },

    formatCurrency(value) {
      if (value == null || value === '') return '—';
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value);
    },

    metodoPagoLabel(id) {
      const mp = this.metodosPago.find(m => Number(m.id) === Number(id));
      return mp ? mp.nombre : (id ?? '—');
    },
  },
};
</script>

<style scoped>
.indicador {
  border-radius: 8px;
  padding: 8px 16px;
  min-width: 150px;
  text-align: center;
}
.indicador-rojo {
  background-color: #fff5f5;
  border: 1px solid #f5c6cb;
}
.indicador-verde {
  background-color: #f0fff4;
  border: 1px solid #c3e6cb;
}
.indicador-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6c757d;
}
.indicador-rojo .indicador-valor {
  font-size: 15px;
  font-weight: 700;
  color: #dc3545;
}
.indicador-verde .indicador-valor {
  font-size: 15px;
  font-weight: 700;
  color: #28a745;
}

.icon-btn { width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; }
.icon-btn svg { width: 14px; height: 14px; }

.table th, .table td { vertical-align: middle; font-size: 13px; }
</style>
